import * as React from 'react';
import { getAuthToken } from '../lib/apiClient';

export interface ProtectedBlobState {
  blob: Blob | null;
  loading: boolean;
  error: boolean;
}

/**
 * Fetches a token-protected asset and returns its raw Blob - e.g. to render a
 * .docx with docx-preview, or to build an object URL for an <img>. Unlike
 * useProtectedImageUrl (which yields an object URL), this hands back the Blob so
 * callers can choose how to consume it. Returns { blob, loading, error }.
 */
export function useProtectedBlob(url: string | null | undefined): ProtectedBlobState {
  const [state, setState] = React.useState<ProtectedBlobState>({
    blob: null,
    loading: false,
    error: false,
  });

  React.useEffect(() => {
    if (!url) {
      setState({ blob: null, loading: false, error: false });
      return;
    }

    let cancelled = false;
    setState({ blob: null, loading: true, error: false });
    void (async () => {
      try {
        const token = await getAuthToken();
        if (cancelled) return;
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (cancelled) return;
        if (!res.ok) {
          setState({ blob: null, loading: false, error: true });
          return;
        }
        const blob = await res.blob();
        if (cancelled) return;
        setState({ blob, loading: false, error: false });
      } catch {
        if (!cancelled) setState({ blob: null, loading: false, error: true });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [url]);

  return state;
}
